import React, { useCallback, useEffect, useMemo, useState } from "react";
import AdminService from "../../../api/services/adminService";
import { STAKING_WALLET_HINT, formatWalletType, isStakingWallet } from "../../../utils/walletTypes";
import "./InternalTransferSettingsPanel.css";

const formatWalletName = (wallet) => formatWalletType(wallet);

const normalizeSettings = (data) => {
  if (!data || typeof data !== "object") {
    return {
      userId: "",
      wallet_types: [],
      disabled_wallets: [],
      disabled_routes: [],
      routes: [],
    };
  }
  return {
    userId: data.userId != null ? String(data.userId) : "",
    wallet_types: Array.isArray(data.wallet_types)
      ? data.wallet_types.map((w) => String(w ?? "").trim()).filter(Boolean)
      : [],
    disabled_wallets: Array.isArray(data.disabled_wallets)
      ? data.disabled_wallets.map((w) => String(w ?? "").trim()).filter(Boolean)
      : [],
    disabled_routes: Array.isArray(data.disabled_routes) ? data.disabled_routes : [],
    routes: Array.isArray(data.routes)
      ? data.routes
          .map((r) => ({
            from: String(r?.from ?? "").trim(),
            to: String(r?.to ?? "").trim(),
            enabled: r?.enabled !== false,
          }))
          .filter((r) => r.from && r.to)
      : [],
  };
};

const InternalTransferSettingsPanel = ({ userId }) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busyKey, setBusyKey] = useState("");

  const applyResponseData = useCallback((data) => {
    setSettings(normalizeSettings(data));
  }, []);

  const loadSettings = useCallback(async () => {
    if (!userId) {
      setError("User ID missing.");
      setSettings(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    setNotice("");
    try {
      const res = await AdminService.getUserInternalTransferSettings(userId);
      if (res?.success) {
        applyResponseData(res.data);
      } else {
        setError(res?.message || "Failed to load internal transfer settings.");
        setSettings(null);
      }
    } catch {
      setError("Failed to load internal transfer settings.");
      setSettings(null);
    } finally {
      setLoading(false);
    }
  }, [userId, applyResponseData]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const walletTypes = useMemo(() => {
    if (settings?.wallet_types?.length) return settings.wallet_types;
    const fromRoutes = new Set();
    (settings?.routes || []).forEach((r) => {
      if (r.from) fromRoutes.add(r.from);
      if (r.to) fromRoutes.add(r.to);
    });
    return Array.from(fromRoutes);
  }, [settings]);

  const disabledWalletSet = useMemo(
    () => new Set(settings?.disabled_wallets || []),
    [settings]
  );

  const routesByFrom = useMemo(() => {
    const map = new Map();
    (settings?.routes || []).forEach((route) => {
      if (!map.has(route.from)) map.set(route.from, []);
      map.get(route.from).push(route);
    });
    return map;
  }, [settings]);

  const handleUpdate = async (body, key) => {
    if (!userId || busyKey) return;
    setBusyKey(key);
    setError("");
    setNotice("");
    try {
      const res = await AdminService.updateUserInternalTransferSettings(body);
      if (res?.success) {
        applyResponseData(res.data);
        setNotice(res.message || "Internal transfer settings updated.");
      } else {
        setError(res?.message || "Failed to update internal transfer settings.");
      }
    } catch {
      setError("Failed to update internal transfer settings.");
    } finally {
      setBusyKey("");
    }
  };

  const handleRouteToggle = (route) => {
    handleUpdate(
      {
        userId,
        fromWallet: route.from,
        toWallet: route.to,
        enabled: !route.enabled,
      },
      `route:${route.from}:${route.to}`
    );
  };

  const handleWalletToggle = (wallet) => {
    const currentlyOff = disabledWalletSet.has(wallet);
    handleUpdate(
      {
        userId,
        fromWallet: wallet,
        enabled: currentlyOff,
      },
      `wallet:${wallet}`
    );
  };

  if (loading) {
    return (
      <div className="its-panel its-panel--status">
        <i className="fas fa-spinner fa-spin" /> Loading transfer settings…
      </div>
    );
  }

  if (error && !settings) {
    return (
      <div className="its-panel">
        <div className="its-panel__alert its-panel__alert--error">
          <i className="fas fa-exclamation-circle" /> {error}
        </div>
        <button type="button" className="its-panel__retry" onClick={loadSettings}>
          Retry
        </button>
      </div>
    );
  }

  const routes = settings?.routes || [];

  return (
    <div className="its-panel">
      {error && (
        <div className="its-panel__alert its-panel__alert--error">
          <i className="fas fa-exclamation-circle" /> {error}
        </div>
      )}
      {notice && (
        <div className="its-panel__alert its-panel__alert--success">
          <i className="fas fa-check-circle" /> {notice}
        </div>
      )}

      <p className="its-panel__hint">
        Default is all transfers allowed. Off means this user cannot move funds on that
        route. Staking wallet transfers stay off — that balance can only buy staking packages.
      </p>

      {walletTypes.length > 0 && (
        <section className="its-panel__section">
          <h4 className="its-panel__section-title">Wallet master</h4>
          <p className="its-panel__section-hint">
            Off blocks all internal transfers involving that wallet for this user.
          </p>
          <div className="its-panel__wallets">
            {walletTypes.map((wallet) => {
              const locked = isStakingWallet(wallet);
              const enabled = locked ? false : !disabledWalletSet.has(wallet);
              const key = `wallet:${wallet}`;
              const busy = busyKey === key;
              return (
                <div key={wallet} className="its-panel__wallet">
                  <span className="its-panel__wallet-name">
                    {formatWalletName(wallet)}
                    {locked ? <small className="its-panel__lock">Locked</small> : null}
                  </span>
                  <label
                    className="traders-toggle traders-toggle--sm"
                    title={
                      locked
                        ? STAKING_WALLET_HINT
                        : enabled
                          ? `${formatWalletName(wallet)} transfers on`
                          : `${formatWalletName(wallet)} transfers off`
                    }
                  >
                    <input
                      type="checkbox"
                      checked={enabled}
                      onChange={() => handleWalletToggle(wallet)}
                      disabled={Boolean(busyKey) || locked}
                      aria-label={`${formatWalletName(wallet)} wallet transfers ${enabled ? "on" : "off"}`}
                    />
                    <span className="traders-toggle__slider" />
                  </label>
                  <span className={`its-panel__state ${enabled ? "is-on" : "is-off"}`}>
                    {busy ? <i className="fas fa-spinner fa-spin" aria-hidden /> : enabled ? "On" : "Off"}
                  </span>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <section className="its-panel__section">
        <h4 className="its-panel__section-title">Transfer routes</h4>
        {routes.length === 0 ? (
          <p className="its-panel__empty">No transfer routes returned. All transfers are allowed by default.</p>
        ) : (
          <div className="its-panel__groups">
            {Array.from(routesByFrom.entries()).map(([fromWallet, fromRoutes]) => (
              <div key={fromWallet} className="its-panel__group">
                <div className="its-panel__group-head">From {formatWalletName(fromWallet)}</div>
                <ul className="its-panel__routes">
                  {fromRoutes.map((route) => {
                    const key = `route:${route.from}:${route.to}`;
                    const busy = busyKey === key;
                    const locked = isStakingWallet(route.from) || isStakingWallet(route.to);
                    const enabled = locked ? false : route.enabled;
                    return (
                      <li key={key} className="its-panel__route">
                        <span className="its-panel__route-label">
                          {formatWalletName(route.from)}
                          <i className="fas fa-arrow-right" aria-hidden />
                          {formatWalletName(route.to)}
                          {locked ? <small className="its-panel__lock">Locked</small> : null}
                        </span>
                        <label
                          className="traders-toggle traders-toggle--sm"
                          title={
                            locked
                              ? STAKING_WALLET_HINT
                              : `${formatWalletName(route.from)} → ${formatWalletName(route.to)}`
                          }
                        >
                          <input
                            type="checkbox"
                            checked={enabled}
                            onChange={() => handleRouteToggle(route)}
                            disabled={Boolean(busyKey) || locked}
                            aria-label={`${formatWalletName(route.from)} to ${formatWalletName(route.to)} ${enabled ? "on" : "off"}`}
                          />
                          <span className="traders-toggle__slider" />
                        </label>
                        <span className={`its-panel__state ${enabled ? "is-on" : "is-off"}`}>
                          {busy ? (
                            <i className="fas fa-spinner fa-spin" aria-hidden />
                          ) : enabled ? (
                            "On"
                          ) : (
                            "Off"
                          )}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default InternalTransferSettingsPanel;
